import React, { useEffect, useState } from 'react';
import { MODO_POR_DEFECTO, ModoTiendas } from '../utils/modoTiendas';
import { useEsMovil } from '../hooks/useEsMovil';
import { etiquetaPeriodo, Periodo } from '../utils/periodo';

/**
 * Vistas del tablero. `'meta'` (Presupuesto) se agregó en 2026-08; el Header
 * compara con `=== 'global'` / `=== 'tienda'` explícitos, así que un tercer
 * valor no reactiva por accidente ningún control de las otras dos.
 */
export type IndicadoresView = 'global' | 'tienda' | 'meta';

export interface HeaderProps {
  view: IndicadoresView;
  onGlobal: () => void;
  onTienda: () => void;
  /** Opcional para no romper a consumidores/pruebas anteriores a la vista de Presupuesto. */
  onMeta?: () => void;
  /**
   * Switch de alcance "Activas en la herramienta" / "Sin uso de la
   * herramienta" / "Todas las tiendas": solo tiene sentido en la vista Global
   * — se ignora (y el control no se renderiza) en Mi tienda. Ambos opcionales
   * para no romper a consumidores/pruebas que no les importa este control.
   *
   * Los tres estados son COMPLEMENTARIOS: activas + sinUso = todas, siempre.
   * Ver `utils/modoTiendas.ts`.
   */
  modo?: ModoTiendas;
  onModoChange?: (value: ModoTiendas) => void;
  /**
   * Periodo APLICADO de la vista (12-oct: el mismo estado en Global y Tienda).
   * En vivo, la pill verde «En vivo · hoy HH:MM» con el punto pulsante; con un
   * rango, la pill neutra «Periodo · 05/10/2026 – 11/10/2026», CON AÑO: sin él
   * nadie podía ver que el campo había quedado en el año 0026. Sin la prop no
   * se pinta pill (consumidores anteriores).
   */
  periodo?: Periodo;
}

/**
 * Ajustes que sólo aplican en teléfono: altura tocable y texto centrado.
 * 44 px es el mínimo recomendado para un dedo; las pestañas daban 37 px y el
 * conmutador de alcance 30 px, medidos en la auditoría.
 */
const tocableMovil: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 12px',
  fontSize: 13.5,
  width: '100%',
  textAlign: 'center',
};


const activeBtnStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  padding: '8px 22px',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 800,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: '#22349b',
  color: '#fff',
  boxShadow: '0 4px 12px -4px rgba(34,52,155,.6)',
};

const idleBtnStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  padding: '8px 22px',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: 'transparent',
  color: '#5b616d',
};

/** Versión reducida de `activeBtnStyle`/`idleBtnStyle` para el switch "Activas en la herramienta / Todas las tiendas" (más chico que Global/Mi tienda, mismo lenguaje visual). */
const activeToggleStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  padding: '6px 14px',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: '#22349b',
  color: '#fff',
};

const idleToggleStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  padding: '6px 14px',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: 'transparent',
  color: '#5b616d',
};

/**
 * Header del tablero hi-fi: logo COLECTIVO/MODA + hexágono, switch
 * segmentado Global/Mi tienda y, a la derecha: en Global, el switch "Activas
 * en la herramienta / Sin uso / Todas" (feedback del PO); y en las dos vistas
 * la pill del periodo APLICADO («En vivo · hoy HH:MM» o «Periodo · dd/mm/aaaa –
 * dd/mm/aaaa»). Recreado de `Tablero Colectivo Moda.dc.html` (líneas 29-47).
 *
 * La pill volvió a Global el 12-oct: con borrador y «Aplicar», los campos de
 * fecha pueden mostrar algo que todavía NO se aplicó, y la pill es lo que dice
 * qué periodo tienen las cifras.
 */
const Header: React.FC<HeaderProps> = ({
  view,
  onGlobal,
  onTienda,
  onMeta,
  modo = MODO_POR_DEFECTO,
  onModoChange = () => {},
  periodo,
}) => {
  const esMovil = useEsMovil();
  // El reloj de «En vivo · hoy HH:MM» se refresca cada 30 s.
  const [ahora, setAhora] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setAhora(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const isGlobal = view === 'global';
  const isTienda = view === 'tienda';
  const isMeta = view === 'meta';

  return (
    <div
      style={{
        display: 'flex',
        // En movil se apila: los tres bloques (marca, pestañas, alcance) no
        // caben en una fila de 390 px y antes se salian de la pantalla, con la
        // cabecera fija de la aplicacion robandoles el clic.
        flexDirection: esMovil ? 'column' : 'row',
        alignItems: esMovil ? 'stretch' : 'center',
        gap: esMovil ? 12 : 20,
        background: '#fff',
        border: '1px solid #e7e9ef',
        borderRadius: 18,
        padding: esMovil ? '12px 14px' : '13px 22px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05),0 16px 38px -26px rgba(20,28,54,.55)',
      }}
    >
      <div style={{ flex: esMovil ? 'none' : 1, display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 0.9 }}>
          <span style={{ fontWeight: 300, fontSize: 19, letterSpacing: '.16em' }}>
            COLECTIVO
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 3 }}>
            <span style={{ fontWeight: 800, fontSize: 30, letterSpacing: '.03em' }}>
              MODA
            </span>
            <svg
              width="25"
              height="29"
              viewBox="0 0 26 30"
              fill="none"
              stroke="#14171d"
              strokeWidth={1.5}
              strokeLinejoin="round"
            >
              <polygon points="13,1.2 24,7.6 24,22.4 13,28.8 2,22.4 2,7.6" />
              <polyline points="13,1.2 13,10.4 24,7.6" />
              <polyline points="13,10.4 2,7.6" />
              <polyline points="13,10.4 13,28.8" />
            </svg>
          </span>
        </div>
      </div>

      <div
        style={{
          display: esMovil ? 'grid' : 'inline-flex',
          // Tres columnas iguales: en movil las pestañas son el control
          // principal de la pantalla y deben ocupar todo el ancho, no
          // apelotonarse a la izquierda.
          gridTemplateColumns: esMovil ? 'repeat(3, 1fr)' : undefined,
          background: '#eef0f5',
          border: '1px solid #e2e5ee',
          borderRadius: 999,
          padding: 4,
        }}
      >
        <button type="button" onClick={onGlobal} style={{ ...(isGlobal ? activeBtnStyle : idleBtnStyle), ...(esMovil ? tocableMovil : null) }}>
          Global
        </button>
        <button type="button" onClick={onTienda} style={{ ...(isTienda ? activeBtnStyle : idleBtnStyle), ...(esMovil ? tocableMovil : null) }}>
          Tienda
        </button>
        {onMeta && (
          <button type="button" onClick={onMeta} style={{ ...(isMeta ? activeBtnStyle : idleBtnStyle), ...(esMovil ? tocableMovil : null) }}>
            Presupuesto
          </button>
        )}
      </div>

      <div
        style={{
          flex: esMovil ? 'none' : 1,
          display: 'flex',
          // En móvil, el alcance (sólo Global) y el periodo se apilan.
          flexDirection: esMovil ? 'column' : 'row',
          justifyContent: esMovil ? 'stretch' : 'flex-end',
          alignItems: esMovil ? 'stretch' : 'center',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        {isGlobal && (
          <div
            role="group"
            aria-label="Alcance de tiendas"
            style={{
              display: esMovil ? 'grid' : 'inline-flex',
              // Apilado en movil: los tres textos son largos ("Activas en la
              // herramienta") y en una fila de 390 px quedaban en 30 px de alto
              // y varios cientos de ancho, fuera de la pantalla.
              gridTemplateColumns: esMovil ? '1fr' : undefined,
              width: esMovil ? '100%' : undefined,
              background: '#eef0f5',
              border: '1px solid #e2e5ee',
              borderRadius: esMovil ? 14 : 999,
              padding: 3,
              gap: esMovil ? 2 : 0,
            }}
          >
            <button
              type="button"
              data-testid="modo-activas"
              onClick={() => onModoChange('activas')}
              style={{ ...(modo === 'activas' ? activeToggleStyle : idleToggleStyle), ...(esMovil ? tocableMovil : null) }}
            >
              Activas en la herramienta
            </button>
            <button
              type="button"
              data-testid="modo-sin-uso"
              onClick={() => onModoChange('sinUso')}
              style={{ ...(modo === 'sinUso' ? activeToggleStyle : idleToggleStyle), ...(esMovil ? tocableMovil : null) }}
            >
              Sin uso de la herramienta
            </button>
            <button
              type="button"
              data-testid="modo-todas"
              onClick={() => onModoChange('todas')}
              style={{ ...(modo === 'todas' ? activeToggleStyle : idleToggleStyle), ...(esMovil ? tocableMovil : null) }}
            >
              Todas las tiendas
            </button>
          </div>
        )}
        {periodo?.modo === 'vivo' && (
          <span
            data-testid="header-periodo"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: '#eafaf0',
              border: '1px solid #bfe9cf',
              color: '#0f7a3b',
              fontWeight: 700,
              fontSize: 13,
              padding: '8px 15px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#17a44d',
                animation: 'cmpulse 1.6s infinite',
              }}
            />
            {etiquetaPeriodo(periodo, ahora)}
          </span>
        )}
        {periodo?.modo === 'rango' && (
          <span
            data-testid="header-periodo"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: '#eef0f5',
              border: '1px solid #e2e5ee',
              color: '#5b616d',
              fontWeight: 700,
              fontSize: 13,
              padding: '8px 15px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            {etiquetaPeriodo(periodo, ahora)}
          </span>
        )}
      </div>
    </div>
  );
};

export default Header;
